import "./styles.scss";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  FieldLabeledProps,
  ContactInformationProps,
  ContactInformationFieldValue,
} from "../../types/fieldTypes";
import TextField from "../../BaseInputFields/TextField/TextField";
import { useEffect, useState } from "react";
import PhoneNumberField from "../PhoneField/PhoneNumberField";
import { SimpleAddressField } from "../SimpleAddressField";
import EmailField from "../EmailField/EmailField";
import { EnumSingleSelectFieldV2 } from "../../DropdownFields/EnumSingleSelectFieldV2/EnumSingleSelectFieldV2";
import {
  ContactMethodTypes,
  contactMethodTypesOptions,
} from "../../../../lib/contactMethodTypes";
import { getErrorMessagesFromErrorMessages } from "../../../../utils/field";

export interface ContactInformationFieldProps extends ContactInformationProps {
  availableContactMethods?: ContactMethodTypes[];
  availablePreferredContactMethods?: ContactMethodTypes[];
  labelForPhoneNumberInput?: string;
  labelForPhoneExtensionInput?: string;
  showAdditionalPhoneNumberInput?: boolean;
  labelForAdditionalPhoneNumberInput?: string;
  showMobilePhoneNumberInput?: boolean;
  labelForMobilePhoneNumberInput?: string;
  labelForEmailInput?: string;
  labelForFaxNumberInput?: string;
  labelForMailingAddressInput?: string;
  labelProps?: FieldLabeledProps;
  requireDefaultContactMethod?: boolean;
  showDefaultContactMethodInput?: boolean;
  labelForDefaultContactMethodInput?: string;
  restrictPhoneNumberExtensionToNumeric?: boolean;

  customAddressFieldComponent?: React.FC<any>;
}

const defaultPropValues: ContactInformationFieldProps = {
  labelForPhoneNumberInput: "Phone Number",
  labelForPhoneExtensionInput: "Phone Extension",
  showAdditionalPhoneNumberInput: false,
  labelForAdditionalPhoneNumberInput: "Additional Phone Number",
  showMobilePhoneNumberInput: false,
  labelForMobilePhoneNumberInput: "Mobile Phone Number",
  labelForEmailInput: "Email",
  labelForFaxNumberInput: "Fax Number",
  labelForMailingAddressInput: "Mailing Address",
  showDefaultContactMethodInput: true,
  labelForDefaultContactMethodInput: "Preferred Contact Method",
  requireDefaultContactMethod: true,
  availableContactMethods: [
    ContactMethodTypes.Phone,
    ContactMethodTypes.Email,
    ContactMethodTypes.Mail,
  ],
  availablePreferredContactMethods: [
    ContactMethodTypes.Phone,
    ContactMethodTypes.Email,
  ],
  restrictPhoneNumberExtensionToNumeric: true,
};
export const ContactInformationField: React.FC<ContactInformationFieldProps> = (
  props
) => {
  const [localContactInformation, setLocalContactInformation] = useState<
    ContactInformationFieldValue | undefined
  >(props.value);

  useEffect(() => {
    setLocalContactInformation(props.value);
  }, [props.value]);

  const onContactInformationChangeHandler = (
    contactInformation: ContactInformationFieldValue
  ) => {
    setLocalContactInformation(contactInformation);
    props.onSubmit?.(contactInformation);
  };

  props = { ...defaultPropValues, ...props };

  const availablePreferredContactMethods =
    props.availablePreferredContactMethods?.filter((x) =>
      props.availableContactMethods?.includes(x)
    );

  const filteredPreferredContactMethodOptions =
    contactMethodTypesOptions.filter((x) =>
      availablePreferredContactMethods?.includes(x.value)
    );

  const showDefaultContactMethodInput =
    props.showDefaultContactMethodInput !== false;

  const AddressFieldComponent =
    props.customAddressFieldComponent ?? SimpleAddressField;

  return (
    <div className="ucl-contact-information-field">
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localContactInformation }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      {showDefaultContactMethodInput && (
        <div className="ucl-contact-information-field_input contact-method">
          <EnumSingleSelectFieldV2
            label={
              props.labelForDefaultContactMethodInput ??
              "Preferred Contact Method"
            }
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "PreferredContactMethod"
            )}
            disabled={props.disabled}
            isRequired={!!props.requireDefaultContactMethod}
            optionValues={filteredPreferredContactMethodOptions}
            type="EnumSingleSelect"
            value={localContactInformation?.preferredContactMethod}
            onSubmit={(preferredContactMethod: any) =>
              onContactInformationChangeHandler({
                ...localContactInformation,
                preferredContactMethod,
              })
            }
          />
        </div>
      )}
      {props.availableContactMethods?.includes(ContactMethodTypes.Phone) && (
        <div className="ucl-contact-information-field_input phonefax-number">
          <PhoneNumberField
            disabled={props.disabled}
            value={localContactInformation?.phoneNumber}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "PhoneNumber"
            )}
            isRequired={
              !!localContactInformation?.preferredContactMethod &&
              localContactInformation?.preferredContactMethod ===
                ContactMethodTypes.Phone
            }
            label={props.labelForPhoneNumberInput ?? "Phone Number"}
            onSubmit={(phoneNumber) =>
              onContactInformationChangeHandler({
                ...localContactInformation,
                phoneNumber,
              })
            }
          />
          <TextField
            inputMode={
              props.restrictPhoneNumberExtensionToNumeric ? "numeric" : "text"
            }
            disabled={props.disabled}
            value={localContactInformation?.phoneNumberExtension}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "PhoneNumberExtension"
            )}
            label={props.labelForPhoneExtensionInput ?? "Phone Extension"}
            onSubmit={(phoneNumberExtension) =>
              onContactInformationChangeHandler({
                ...localContactInformation,
                phoneNumberExtension,
              })
            }
          />
          {props.showAdditionalPhoneNumberInput && (
            <PhoneNumberField
              disabled={props.disabled}
              value={localContactInformation?.additionalPhoneNumber}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "AdditionalPhoneNumber"
              )}
              label={
                props.labelForAdditionalPhoneNumberInput ??
                "Additional Phone Number"
              }
              onSubmit={(additionalPhoneNumber) =>
                onContactInformationChangeHandler({
                  ...localContactInformation,
                  additionalPhoneNumber,
                })
              }
            />
          )}
          {props.showMobilePhoneNumberInput && (
            <PhoneNumberField
              disabled={props.disabled}
              value={localContactInformation?.mobilePhoneNumber}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "MobilePhoneNumber"
              )}
              label={
                props.labelForMobilePhoneNumberInput ?? "Mobile Phone Number"
              }
              onSubmit={(mobilePhoneNumber) =>
                onContactInformationChangeHandler({
                  ...localContactInformation,
                  mobilePhoneNumber,
                })
              }
            />
          )}
          {props.availableContactMethods?.includes(ContactMethodTypes.Fax) && (
            <PhoneNumberField
              disabled={props.disabled}
              value={localContactInformation?.faxNumber}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "FaxNumber"
              )}
              isRequired={
                !!localContactInformation?.preferredContactMethod &&
                localContactInformation?.preferredContactMethod ===
                  ContactMethodTypes.Fax
              }
              label={props.labelForFaxNumberInput ?? "Fax Number"}
              onSubmit={(faxNumber) =>
                onContactInformationChangeHandler({
                  ...localContactInformation,
                  faxNumber,
                })
              }
            />
          )}
        </div>
      )}
      {props.availableContactMethods?.includes(ContactMethodTypes.Email) && (
        <div className="ucl-contact-information-field_input email">
          <EmailField
            disabled={props.disabled}
            value={localContactInformation?.email}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "Email"
            )}
            isRequired={
              !!localContactInformation?.preferredContactMethod &&
              localContactInformation?.preferredContactMethod ===
                ContactMethodTypes.Email
            }
            label={props.labelForEmailInput ?? "Email"}
            onSubmit={(email) =>
              onContactInformationChangeHandler({
                ...localContactInformation,
                email,
              })
            }
          />
        </div>
      )}
      {props.availableContactMethods?.includes(ContactMethodTypes.Mail) && (
        <div className="ucl-contact-information-field_input mail">
          <AddressFieldComponent
            label={props.labelForMailingAddressInput ?? "Mailing Address"}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "MailingAddress"
            )}
            isRequired={
              !!localContactInformation?.preferredContactMethod &&
              localContactInformation?.preferredContactMethod ===
                ContactMethodTypes.Mail
            }
            disabled={props.disabled}
            value={localContactInformation?.mailingAddress}
            onSubmit={(mailingAddress) =>
              onContactInformationChangeHandler({
                ...localContactInformation,
                mailingAddress,
              })
            }
          />
        </div>
      )}
    </div>
  );
};

export default ContactInformationField;
